import { inject, Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

const ICON_SETS = [
  { namespace: null, url: 'assets/icons/material-twotone.svg' },
  { namespace: 'mat_outline', url: 'assets/icons/material-outline.svg' },
  { namespace: 'mat_solid', url: 'assets/icons/material-solid.svg' },
  { namespace: 'feather', url: 'assets/icons/feather.svg' },
  { namespace: 'heroicons_outline', url: 'assets/icons/heroicons-outline.svg' },
  { namespace: 'heroicons_solid', url: 'assets/icons/heroicons-solid.svg' },
  { namespace: 'heroicons_mini', url: 'assets/icons/heroicons-mini.svg' },
] as const;

interface IndividualIcon {
  name: string;
  url: string;
  /** Omit to register in the default namespace, alongside the material-twotone set. */
  namespace?: string;
}

const INDIVIDUAL_ICONS: readonly IndividualIcon[] = [
  { name: 'pallet', url: 'assets/icons/pallet-custom.svg' },
  { name: 'coins', url: 'assets/icons/two-coins.svg' },
  { name: 'scan-eye', url: 'assets/icons/scan-eye.svg' },
  /*
    Wizard-stepper badge glyphs, exported straight from the Figma design system (Lucide: 1.33
    stroke, round caps, 12px grid). Namespaced on purpose: an individual icon named `check` would
    shadow the material set's `check` for every `<mat-icon>check</mat-icon>` in the app, and the
    heroicons variant is drawn at half this stroke weight so the two cannot be swapped freely.
  */
  { namespace: 'lucide', name: 'check', url: 'assets/icons/lucide-check.svg' },
  { namespace: 'lucide', name: 'circle-dashed', url: 'assets/icons/lucide-circle-dashed.svg' },
  { namespace: 'lucide', name: 'x', url: 'assets/icons/lucide-x.svg' },
];

@Injectable({ providedIn: 'root' })
export class IconsService {
  readonly #domSanitizer = inject(DomSanitizer);
  readonly #matIconRegistry = inject(MatIconRegistry);

  constructor() {
    this.#registerIcons();
  }

  #registerIcons(): void {
    for (const iconSet of ICON_SETS) {
      const safeUrl = this.#domSanitizer.bypassSecurityTrustResourceUrl(iconSet.url);
      if (iconSet.namespace) {
        this.#matIconRegistry.addSvgIconSetInNamespace(iconSet.namespace, safeUrl);
      } else {
        this.#matIconRegistry.addSvgIconSet(safeUrl);
      }
    }

    for (const icon of INDIVIDUAL_ICONS) {
      const safeUrl = this.#domSanitizer.bypassSecurityTrustResourceUrl(icon.url);
      if (icon.namespace) {
        this.#matIconRegistry.addSvgIconInNamespace(icon.namespace, icon.name, safeUrl);
      } else {
        this.#matIconRegistry.addSvgIcon(icon.name, safeUrl);
      }
    }
  }
}
